import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant, useTenantUploadedFileTypes } from "hooks/settings";
import {
  useListUploadedFilesQuery,
  useGetUploadedFileQuery,
} from "src/store/api/generatedApi";
import { UploadedFileType } from "./upload/uploadedFileType";
import { useUploadFile } from "./useUploadFile";
import { FileManagerFile } from "./types";
import {
  convertUploadedFilesToFileManager,
  processDownloadedFile,
} from "./helpers";

export const useFileExplorer = () => {
  const [selectedFileType, setSelectedFileType] = useState(
    "adhoc" as UploadedFileType
  );
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();
  const tenant = useTenant();
  const { data: fileTypeOptions = [] } = useTenantUploadedFileTypes();
  const { handleUpload: uploadFile, uploadLoading } = useUploadFile();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [downloadFileId, setDownloadFileId] = useState<number | null>(null);
  const downloadingFileRef = useRef<FileManagerFile | null>(null);
  const lastProcessedIdRef = useRef<number | null>(null);

  const {
    data: uploadedFiles = [],
    isLoading,
    refetch,
  } = useListUploadedFilesQuery({
    tenantName: tenant,
    types: [selectedFileType],
    deleted: false,
    limit: 1000,
  });

  const { data: downloadedFile, isSuccess: downloadSuccess } =
    useGetUploadedFileQuery(
      {
        tenantName: tenant,
        uploadedFileId: downloadFileId ?? 0,
      },
      {
        skip: downloadFileId === null,
      }
    );

  useEffect(() => {
    if (
      !downloadSuccess ||
      !downloadingFileRef.current ||
      downloadFileId === null ||
      lastProcessedIdRef.current === downloadFileId
    ) {
      return;
    }
    lastProcessedIdRef.current = downloadFileId;
    const file = downloadingFileRef.current;
    try {
      processDownloadedFile(downloadedFile.file.content, file.name);
      notifyStatus({
        text: `Downloaded ${file.name}`,
        type: "success",
      });
    } catch (error) {
      console.error("Download failed:", error);
      notifyStatus({
        text: `Failed to download ${file.name}`,
        type: "error",
      });
    }
    downloadingFileRef.current = null;
  }, [downloadFileId, downloadSuccess, downloadedFile, notifyStatus]);

  const fileManagerFiles: FileManagerFile[] = useMemo(() => {
    return convertUploadedFilesToFileManager(uploadedFiles);
  }, [uploadedFiles]);

  const handleFileUpload = useCallback(
    async (files: FileList) => {
      for (const file of files) {
        await uploadFile(file, selectedFileType);
      }
      setTimeout(() => {
        void refetch();
      }, 1000);
    },
    [uploadFile, refetch, selectedFileType]
  );

  const handleFileDownload = useCallback((files: FileManagerFile[]) => {
    for (const file of files) {
      if (file.id) {
        downloadingFileRef.current = file;
        lastProcessedIdRef.current = null;
        setDownloadFileId(file.id);
      }
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      notifyStatus({
        text: t("filesRefreshed") || "Files refreshed successfully",
        type: "success",
      });
    } catch {
      notifyStatus({
        text: t("filesRefreshError") || "Failed to refresh files",
        type: "error",
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch, notifyStatus, t]);

  return {
    files: fileManagerFiles,
    isLoading: isLoading || uploadLoading,
    isRefreshing,
    onFileUpload: handleFileUpload,
    onFileDownload: handleFileDownload,
    onRefresh: handleRefresh,
    uploadLoading,
    selectedFileType,
    setSelectedFileType,
    fileTypeOptions,
  };
};
